import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AddressableViewLayout } from 'framework/ViewTypes';
import { bindCollection } from 'framework/ViewTypes/contentLayout';
import type { SourcingFilters } from '../types';
import { useRecruitmentPoolSourcingFilterBar } from './filters/useRecruitmentPoolSourcingFilterBar';
import { useSourcingCandidatesTableConfig } from './sourcingCandidatesTableConfig';
import useRecruitmentPoolSourcingCollection from './useRecruitmentPoolSourcingCollection';
import useRecruitmentPoolSourcingOptions from './useRecruitmentPoolSourcingOptions';
import { useAuthorization } from 'framework/authorization';
import { RECRUITING_SOURCING_SAVE, RECRUITING_SOURCING_DELETE } from '../constants';
import { mapSourcingFiltersToCollectionFilters } from './filters/utils/sourcingFilterModel';

const RecruitmentPoolSourcingPage = () => {
  const { isGranted } = useAuthorization();
  const { id: recruitmentPoolId } = useParams<{ id: string }>();
  const { filterOptions, isLoading: isLoadingOptions } = useRecruitmentPoolSourcingOptions(recruitmentPoolId);
  const collection = useRecruitmentPoolSourcingCollection({ recruitmentPoolId, filterOptions });
  const setCollectionFilters = collection.setFilters;
  const applySourcingFilters = useCallback(
    (filters: SourcingFilters) => setCollectionFilters(mapSourcingFiltersToCollectionFilters(filters)),
    [setCollectionFilters],
  );

  const pageLoading = isLoadingOptions || collection.isLoading;

  const { filterProps, drawer } = useRecruitmentPoolSourcingFilterBar({
    recruitmentPoolId: recruitmentPoolId!,
    filterOptions,
    filters: collection.filters,
    onApply: applySourcingFilters,
    canDelete: isGranted(RECRUITING_SOURCING_DELETE),
    canSave: isGranted(RECRUITING_SOURCING_SAVE),
  });

  const binding = bindCollection(collection, useSourcingCandidatesTableConfig(collection.rows), {
    filterProps,
    totalRecordsLabel: 'Matched candidates',
  });

  return <AddressableViewLayout {...binding.pageProps} loading={pageLoading} drawer={drawer} />;
};

export default RecruitmentPoolSourcingPage;
